import {Component, TemplateRef, ChangeDetectionStrategy} from "@angular/core";
import {NzModalService} from "ng-zorro-antd/modal";
import {NzTableFilterList, NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import {lastValueFrom} from "rxjs";
import {JobItemDto, JobQueryDto, JobsListDto, PerFormaAPIService} from "src/app/services/pfapi.service";
import Utils from "src/utils";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-scan-jobs-tab",
    templateUrl: "./scan-jobs-tab.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScanJobsTabComponent {
    constructor(
        private api: PerFormaAPIService,
        private modal: NzModalService
    ) {}

    jobList: JobsListDto = {Jobs: []};
    loading = true;
    total = 0;
    pageIndex = 1;
    pageSize = 10;
    pageSizeOptions = [5, 10, 20, 50];
    currentJob?: JobItemDto;
    searchValue = "";
    directoryMap: Record<number, string> = {};
    lastParams: NzTableQueryParams | null = null;
    activeFilters: Record<string, string[]> = {};
    directoriesLoaded = false;
    defaultSort: SortDto[] = [
        {key: "JobID", value: null},
        {key: "JobType", value: null},
        {key: "JobCreateTime", value: "descend"},
        {key: "JobEndTime", value: null},
        {key: "JobStatus", value: null}
    ];
    sort: SortDto[] = [...this.defaultSort];
    jobTypeFilters: NzTableFilterList = [
        {text: "Load from Scanner", value: "ImportScan"},
        {text: "Process to PerForma", value: "FinalizeScan"}
    ];
    statusFilters: NzTableFilterList = [
        {text: "Queued", value: "Queued"},
        {text: "Complete", value: "Complete"},
        {text: "Failed", value: "Failed"}
    ];
    downloadingJobs = new Set<number>();
    columns = [
        {
            title: "ID",
            compare: (a: JobItemDto, b: JobItemDto) => (a.JobID ?? 0) - (b.JobID ?? 0),
            fieldName: "JobID",
            width: "70px"
        },
        {
            title: "Job Type",
            compare: (a: JobItemDto, b: JobItemDto) => (a.JobType ?? 0) - (b.JobType ?? 0),
            fieldName: "JobType",
            width: null
        },
        {
            title: "File Name",
            compare: null,
            fieldName: null,
            width: null
        },
        {
            title: "Time Requested",
            compare: (a: JobItemDto, b: JobItemDto) => (a.JobCreateTime ?? "").localeCompare(b.JobCreateTime ?? ""),
            fieldName: "JobCreateTime",
            width: null
        },
        {
            title: "Time Finished",
            compare: (a: JobItemDto, b: JobItemDto) => (a.JobEndTime ?? "").localeCompare(b.JobEndTime ?? ""),
            fieldName: "JobEndTime",
            width: null
        },
        {
            title: "Status",
            compare: (a: JobItemDto, b: JobItemDto) => (a.JobStatus ?? "").localeCompare(b.JobStatus ?? ""),
            fieldName: "JobStatus",
            width: null
        },
        {
            title: "Action",
            compare: null,
            fieldName: null,
            width: null
        }
    ];

    onSearch = async () => {
        this.pageIndex = 1;
        await this.getData();
    };

    clearSearch = async () => {
        this.searchValue = "";
        this.pageIndex = 1;
        await this.getData();
    };

    showJobInfo = (tmpl: TemplateRef<void>, job: JobItemDto) => {
        this.currentJob = job;
        this.modal.create({
            nzTitle: "Job Information",
            nzContent: tmpl,
            nzFooter: null,
            nzWidth: 600
        });
    };

    closeModal = () => {
        this.modal.closeAll();
    };

    getData = async () => {
        this.loading = true;
        const params: JobQueryDto = {
            Page: this.pageIndex,
            PageSize: this.pageSize
        };

        const activeSort = this.sort?.filter(s => s.value !== null) || [...this.defaultSort];
        if (activeSort.length) {
            params.SortField = activeSort[0].key;
            params.SortOrder = activeSort[0].value;
        }

        if (this.searchValue) {
            params.SearchValue = this.searchValue;
        }
        if (this.activeFilters["JobType"]?.length) {
            params.JobTypeFilter = this.activeFilters["JobType"];
        }
        if (this.activeFilters["JobStatus"]?.length) {
            params.StatusFilter = this.activeFilters["JobStatus"];
        }

        try {
            this.jobList = await lastValueFrom(this.api.postApiScanJobs(params));
            this.total = this.jobList.TotalItems ?? 0;
            this.loadDirectories();
        } catch (error) {
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    loadDirectories() {
        if (this.directoriesLoaded) return;
        this.directoriesLoaded = true;
        lastValueFrom(this.api.getApiScanDirectory()).then(dirs => {
            for (const d of dirs) {
                this.directoryMap[d.ScanDirectoryID] = d.ScanDirectoryPath;
            }
        });
    }

    downloadFile = async (job: JobItemDto) => {
        this.downloadingJobs.add(job.JobID);
        try {
            const resp = await lastValueFrom(this.api.getApiScanLogFileJobId(job.JobID, {observe: "response"}));
            Utils.downloadFileContent(resp, false);
        } finally {
            this.downloadingJobs.delete(job.JobID);
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};
        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        if (this.sortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        const newFilters: Record<string, string[]> = {};
        for (const f of params.filter) {
            if (f.value?.length) {
                newFilters[f.key] = f.value;
            }
        }
        if (this.filtersChanged(newFilters)) {
            this.pageIndex = 1;
        }
        this.activeFilters = newFilters;

        this.sort = [...params.sort];
        await this.getData();
    }

    filtersChanged(newFilters: Record<string, string[]>): boolean {
        const keys = new Set([...Object.keys(this.activeFilters), ...Object.keys(newFilters)]);
        for (const key of keys) {
            const oldVal = this.activeFilters[key] ?? [];
            const newVal = newFilters[key] ?? [];
            if (oldVal.length !== newVal.length || !oldVal.every((v, i) => v === newVal[i])) {
                return true;
            }
        }
        return false;
    }

    isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        const activeFilterA = a.filter.filter(f => f.value?.length);
        const activeFilterB = b.filter.filter(f => f.value?.length);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value) &&
            activeFilterA.length === activeFilterB.length &&
            activeFilterA.every(
                (f, i) =>
                    f.key === activeFilterB[i].key && JSON.stringify(f.value) === JSON.stringify(activeFilterB[i].value)
            )
        );
    }

    sortChanged = (sort: SortDto[]): boolean => {
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];
        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    getSortOrder(fieldName: string | null): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }
}
